// Shipping (spec §3.13–3.14): the Shipping schedule — the Perris Shipping Schedule sheet's columns,
// grouped by pickup date — and the Ship log, what left by day. Fed from the Worklist (Schedule
// pickup) or the "Add a line" row at the bottom. Grey cells are the line's, light teal ones the
// pickup's (one value for every row on it), white ones are typed per row. No CO. column (Isaac).

import { useEffect, useMemo, useRef, useState } from "react";
import type { MouseEvent, ReactNode } from "react";
import { CalendarClockIcon, CalendarIcon, PlusIcon, TriangleAlertIcon, TruckIcon, XIcon } from "lucide-react";
import {
  DEFAULT_WAREHOUSES,
  WAREHOUSE_GROUPS,
  addDays,
  carrierPick,
  checkInOut,
  checkPickupEdit,
  countText,
  findFinishedLines,
  findOpenLines,
  planAddLine,
  formatDay,
  formatShortDay,
  inSource,
  inWarehouses,
  localToday,
  NO_HOLIDAYS,
  parseDateInput,
  parseTime,
  timeShown,
  parseWhole,
  poText,
  ptText,
  routingRows,
  rowWarnings,
  savedWarehouses,
  scheduleGroups,
  shipLogGroups,
  shipLogRows,
  shippingMatches,
  lineRef,
  isMyLine,
  keepsCustomer,
  customerFilterOptions,
  shownCustomerText,
  typedTimeShown,
} from "@app/shared";
import type {
  CarrierPick,
  DivisionRef,
  LineSource,
  LineView,
  PickupRecord,
  ScheduleRow,
  ShipLogRow,
  ShippingRowRecord,
  WarehouseGroup,
} from "@app/shared";
import {
  Button,
  Checkbox,
  DataError,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RegisteredPage,
  TableSkeleton,
  ToolbarButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useMe,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { AskAiBar } from "./ask-ai-bar";
import { useShippingAskAi } from "./axai-shipping";
import { findLines, routingKey, useHolidays, useRoutingData } from "./data";
import { useTableColumns } from "./column-drag";
import { DivisionText } from "./named";
import { Floating, TimeEditor } from "./editors";
import { useViewOnlyNotice } from "./can-write";
import { MessageProvider, useMessage } from "./message";
import { cellAfter, cellKey } from "./cell-keys";
import type { CellKey } from "./cell-keys";
import { refocusEditor } from "./refocus";
import { ShipmentSheet } from "./shipment-sheet";
import {
  CalendarPanel,
  CarrierField,
  CarrierOptions,
  CarriersUnavailable,
  DateField,
  DayInputs,
  dayOfText,
  typedDay,
  useCarrierList,
} from "./pickers";
import type { CarrierSourceApi } from "./pickers";
import { MyCustomersChip, useMyCustomers } from "./my-customers";
import { useShipFlow } from "./ship-flow";
import type { PickupField, PickupInput, RowField } from "./shipping-writer";
import { SourceBadge, SourcePicker, useSourceFilter } from "./source-filter";
import { ColumnsMenu, FilterPill, SearchBox, WarehousePicker } from "./toolbar";
import { useWriter } from "./writer";
import type { LineState } from "./writer";
import { SavingMark, lineStateClass } from "./chips";
import { SelectionBar } from "./selection-bar";

export type ShippingTab = "schedule" | "log";

/** A per-browser convenience (the warehouse), as the Worklist keeps it. */
function storedWarehouses(): WarehouseGroup[] {
  try {
    const raw = localStorage.getItem("routing.warehouses");
    return savedWarehouses(raw ? (JSON.parse(raw) as unknown[]) : DEFAULT_WAREHOUSES);
  } catch {
    return [...DEFAULT_WAREHOUSES];
  }
}

/** `focus`: the line's row, or a pickup's rows, to select and show (the System log's Go to line). */
export function ShippingPage({
  tab,
  shipment,
  focus,
}: {
  tab?: ShippingTab;
  shipment?: string;
  focus?: { line?: string; pickup?: string };
}) {
  return (
    <RegisteredPage id="shipping">
      <MessageProvider>
        <ShippingScreen initialTab={tab ?? "schedule"} openShipment={shipment} focus={focus} />
      </MessageProvider>
    </RegisteredPage>
  );
}

/** The ship-date range of the Ship log: the last 7 days by default (spec §3.14). */
export interface ShipRange {
  preset: "7d" | "30d" | "90d" | "any" | "custom";
  from: string | null;
  to: string | null;
}
export const shipRange = (preset: ShipRange["preset"], today: string): ShipRange =>
  preset === "any" || preset === "custom"
    ? { preset, from: null, to: null }
    : { preset, from: addDays(today, preset === "7d" ? -6 : preset === "30d" ? -29 : -89), to: today };

function ShippingScreen({
  initialTab,
  openShipment,
  focus,
}: {
  initialTab: ShippingTab;
  openShipment?: string;
  focus?: { line?: string; pickup?: string };
}) {
  const today = useMemo(() => localToday(), []);
  // The Ship log's days: the server sends only their shipments (and the schedule).
  const [range, setRange] = useState<ShipRange>(() => shipRange("7d", today));
  const data = useRoutingData("shipping", range);
  const holidays = useHolidays();
  const me = useMe();
  const message = useMessage();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const writer = useWriter(routingKey("shipping", range), who, today);

  const [tab, setTab] = useState<ShippingTab>(initialTab);
  // Division: in the Columns menu, hidden until shown (Isaac's sheet has none).
  const scheduleColumns = useTableColumns("schedule", SCHEDULE_COLUMNS, ["division"]);
  const logColumns = useTableColumns("ship-log", SHIP_LOG_MENU, ["Division"]);
  const [warehouses, setWarehouses] = useState<WarehouseGroup[]>(storedWarehouses);
  const [source, setSource] = useSourceFilter();
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState<string[]>([]);
  // My customers (`my-customers.tsx`): on by default for someone with customers, as on the Worklist.
  const my = useMyCustomers("shipping");
  const mineOk = (line: LineView) => !my.mine || isMyLine(line, my.filter);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [details, setDetails] = useState<string | null>(openShipment ?? null);

  const payload = data.data;
  // A Viewer's Shipping reads as it is: no Add a line, no Mark shipped, no ×, no cell editing.
  const canWrite = payload?.canWrite ?? false;
  const calendar = holidays.data?.calendar ?? NO_HOLIDAYS;
  const lines = useMemo(
    () => (payload ? routingRows(payload, today, calendar).assembled.lines : []),
    [payload, today, calendar],
  );
  const flow = useShipFlow({ writer, payload, lines, today, onDone: () => setSelected({}) });
  // Go to line: once the schedule is there, the line's row (or the pickup's rows) selected and in view.
  const focused = useRef(false);
  useEffect(() => {
    if (focused.current || !payload || (!focus?.line && !focus?.pickup)) return;
    focused.current = true;
    const hit = flow.rows.filter((r) => r.row.lineId === focus.line || r.pickup.id === focus.pickup);
    const [first] = hit;
    if (!first) return;
    setSelected(Object.fromEntries(hit.map((r) => [r.row.id, true])));
    requestAnimationFrame(() =>
      document.querySelector(`[data-schedule-row="${first.row.id}"]`)?.scrollIntoView({ block: "center" }),
    );
  }, [payload, focus, flow.rows]);
  const pickupsById = useMemo(() => new Map((payload?.pickups ?? []).map((p) => [p.id, p])), [payload]);

  const inView = (line: LineView) =>
    mineOk(line) &&
    inSource(line.source, source) &&
    line.whs.some((w) => inWarehouses(w, warehouses)) &&
    keepsCustomer(line, customers);
  const schedule = flow.rows.filter(
    (r) =>
      inView(r.line) &&
      shippingMatches(r.line, search, {
        loadRef: r.pickup.loadRef,
        carrier: r.pickup.carrier,
        wmsAsnTrip: r.row.wmsAsnTrip,
      }),
  );
  /** The shipping row a shipment came from, for its WMS / ASN / TRIP. */
  const shippedRow = (s: ShipLogRow) =>
    (payload?.shippingRows ?? []).find(
      (r) => r.lineId === s.shipment.lineId && r.pickupId === s.shipment.pickupId && r.status === "Shipped",
    );
  const shipLog = shipLogRows(lines).filter(
    (r) =>
      inView(r.line) &&
      (!range.from || (r.shipment.shipDate ?? "") >= range.from) &&
      (!range.to || (r.shipment.shipDate ?? "") <= range.to) &&
      shippingMatches(r.line, search, {
        loadRef: r.shipment.loadRef,
        carrier: r.shipment.carrier,
        tracking: r.shipment.tracking,
        wmsAsnTrip: shippedRow(r)?.wmsAsnTrip,
      }),
  );

  // Ask AI (axai-shipping.ts): what the page shows, read fresh at each question.
  useShippingAskAi({
    today,
    tab,
    scheduleIds: schedule.map((r) => r.row.id),
    shipLogIds: shipLog.map((r) => r.shipment.id),
    mine: my.mine ? my.filter : null,
    describe: [
      source !== "all" ? `source ${source} only` : "",
      my.mine ? "my customers" : "",
      `warehouses ${warehouses.join(" + ") || "all"}`,
      customers.length ? `customers ${customers.join(", ")}` : "",
      search ? `search "${search}"` : "",
      tab === "log" ? `shipped ${range.from ?? "any date"} to ${range.to ?? "today"}` : "",
    ]
      .filter(Boolean)
      .join("; "),
  });

  if (data.isPending || holidays.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  // The counts and lists of the filters follow My customers too.
  const tabLines = (tab === "schedule" ? flow.rows.map((r) => r.line) : shipLogRows(lines).map((r) => r.line)).filter(
    mineOk,
  );
  // The Source filter narrows the Customer list too: one option per shown name (`customerFilterOptions`).
  const pageLines = tabLines.filter((l) => inSource(l.source, source));
  const customerOptions = customerFilterOptions(pageLines);
  const sourceCounts = {
    A2000: tabLines.filter((l) => l.source === "A2000").length,
    Deposco: tabLines.filter((l) => l.source === "Deposco").length,
  };
  const warehouseCounts = Object.fromEntries(
    WAREHOUSE_GROUPS.map((w) => [w, pageLines.filter((l) => l.whs.includes(w)).length]),
  ) as Record<WarehouseGroup, number>;
  const chosen = schedule.filter((r) => selected[r.row.id]);
  const shown = details ? shipLogRows(lines).find((r) => r.shipment.id === details) : undefined;

  return (
    // Room under the last row (Add a line), so a toast in the corner sits below it, not on it.
    <div className="routing-page space-y-3 pb-24">
      <AskAiBar
        mode="shipping"
        version={`${tab}|${source}|${search}|${customers.join()}|${warehouses.join()}|${range.from}|${my.mine}`}
        step={null}
        onBegin={() => {}}
        onEnd={() => {}}
        onUndo={() => {}}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SourcePicker value={source} counts={sourceCounts} onChange={setSource} />
        <WarehousePicker
          value={warehouses}
          counts={warehouseCounts}
          onChange={(w) => {
            setWarehouses(w);
            try {
              localStorage.setItem("routing.warehouses", JSON.stringify(w));
            } catch {
              // Private window: kept for this page only.
            }
          }}
        />
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search customer, PO, PT / SO, load #, carrier, tracking"
        />
        <FilterPill label="Customer" options={customerOptions} selected={customers} onChange={setCustomers} />
        <MyCustomersChip
          hasMine={my.hasMine}
          mine={my.mine}
          count={my.count}
          labels={my.labels}
          onChange={my.setMine}
        />
        {tab === "log" ? <DayRangePicker label="Shipped on" value={range} today={today} onChange={setRange} /> : null}
        {/* Each tab's own columns: shown or hidden and in order, kept per person (`column-drag.tsx`). */}
        {tab === "schedule" ? (
          <ColumnsMenu
            columns={scheduleColumns.ordered}
            order={scheduleColumns.order}
            hidden={scheduleColumns.hidden}
            onChange={scheduleColumns.setHidden}
            onReset={scheduleColumns.reset}
          />
        ) : (
          <ColumnsMenu
            columns={logColumns.ordered}
            order={logColumns.order}
            hidden={logColumns.hidden}
            onChange={logColumns.setHidden}
            onReset={logColumns.reset}
          />
        )}
        <p className="text-muted-foreground ml-auto text-xs">
          {tab === "schedule"
            ? canWrite
              ? "Same columns as the Perris Shipping Schedule sheet · fed from the Worklist (select lines → Schedule pickup) or add a line at the bottom"
              : "Same columns as the Perris Shipping Schedule sheet · fed from the Worklist"
            : "For looking things up · filter by ship date · open a row for the shipment details"}
        </p>
      </div>

      <div role="tablist" aria-label="Shipping" className="flex gap-1 border-b">
        {(
          [
            ["schedule", "Shipping schedule", schedule.length],
            ["log", "Ship log", shipLog.length],
          ] as const
        ).map(([id, label, n]) => (
          <button
            key={id}
            role="tab"
            type="button"
            aria-selected={tab === id}
            onClick={() => setTab(id)}
            className={cn(
              "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm",
              tab === id ? "border-primary font-medium" : "text-muted-foreground border-transparent",
            )}
          >
            {label}
            <span
              className={cn(
                "rounded px-1.5 text-xs tabular-nums",
                tab === id ? "bg-primary text-primary-foreground" : "bg-muted",
              )}
            >
              {n}
            </span>
          </button>
        ))}
      </div>

      {tab === "schedule" ? (
        <>
          <SelectionBar
            active={chosen.length > 0}
            tone="dark"
            hint={canWrite ? "Tick rows to mark shipped or reschedule several at once" : "Tick rows to count them"}
          >
            <span className="font-medium">
              {countText(chosen.length, "row")} selected ·{" "}
              {countText(
                chosen.reduce((n, r) => n + r.line.qty, 0),
                "unit",
              )}
            </span>
            <div className="ml-auto flex gap-1.5">
              {canWrite ? (
                <>
                  <ToolbarButton
                    icon={<TruckIcon />}
                    label="Mark shipped"
                    hint="The selected rows left"
                    variant="secondary"
                    onClick={() => void flow.markShipped(chosen.map((r) => r.line))}
                  />
                  <ToolbarButton
                    icon={<CalendarClockIcon />}
                    label="Reschedule"
                    hint="Change the selected rows' pickup — or move only these lines to a new pickup"
                    variant="secondary"
                    onClick={() => flow.reschedule(chosen)}
                  />
                </>
              ) : null}
              <ToolbarButton
                icon={<XIcon />}
                label="Clear"
                hint="Clear the selection"
                variant="secondary"
                onClick={() => setSelected({})}
              />
            </div>
          </SelectionBar>
          <ScheduleGrid
            columns={scheduleColumns.shown}
            rows={schedule}
            all={flow.rows}
            today={today}
            canWrite={canWrite}
            selected={selected}
            onSelect={setSelected}
            onShip={(rs) => void flow.markShipped(rs.map((r) => r.line))}
            carriers={flow.carriers}
            lineState={writer.lineState}
            onEditPickup={async (pickup, field, value) => {
              const rows = flow.rows.filter((r) => r.pickup.id === pickup.id);
              await flow.shipping.editPickup(pickup, field, value, rows);
            }}
            onEditCarrier={async (pickup, pick) => {
              const rows = flow.rows.filter((r) => r.pickup.id === pickup.id);
              await flow.shipping.editCarrier(pickup, pick, rows);
            }}
            onEditRow={(r, field, value) => void flow.shipping.editRow(r, field, value)}
            onEditPallets={(r, pallets) => writer.saveField(r.line, "pallets", pallets)}
            onRemove={async (r) => {
              const others = flow.rows.filter((x) => x.pickup.id === r.pickup.id && x.row.id !== r.row.id);
              const ok = await message.confirm(
                "Take this line off the pickup?",
                `${r.line.customerName} (${lineRef(r.line)}) goes back to the Worklist with no SCHEDULED SHIP DATE.${others.length ? "" : " No line is left on the pickup, so the pickup is cancelled."}`,
                { confirmLabel: "Take it off" },
              );
              if (ok) await flow.shipping.remove(r, others);
            }}
          />
          {canWrite ? (
            <AddLine
              lines={lines}
              find={async (q) => routingRows(await findLines(q), today, calendar).assembled.lines}
              pickups={payload?.pickups ?? []}
              rowsOf={(id) => flow.rows.filter((r) => r.pickup.id === id).length}
              today={today}
              carriers={flow.carriers}
              onAdd={async (line, target) => {
                return flow.shipping.schedule([line], target, "add-line");
              }}
            />
          ) : null}
          <footer className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span>
              {countText(schedule.length, "line")} on{" "}
              {countText(new Set(schedule.map((r) => r.pickup.id)).size, "pickup")}
            </span>
            <span className="flex items-center gap-1">
              <span className="bg-pickup size-3 rounded-sm border" /> one value for the whole pickup
            </span>
            <span className="flex items-center gap-1">
              <span className="bg-locked size-3 rounded-sm border" /> from the Worklist
            </span>
            <span className="flex items-center gap-1">
              <TriangleAlertIcon className="size-3.5 text-amber-500" /> check this row (hover)
            </span>
            <span className="ml-auto">Shipped lines leave the schedule and go to the Ship log</span>
          </footer>
        </>
      ) : (
        <>
          <ShipLog
            shownIds={logColumns.shown.map((c) => c.id)}
            rows={shipLog}
            pickupsById={pickupsById}
            shippedRow={shippedRow}
            onOpen={(r) => setDetails(r.shipment.id)}
          />
          <footer className="text-muted-foreground flex flex-wrap items-center gap-x-4 text-xs">
            <span>
              {countText(shipLog.length, "shipped line")} · Shipped on{" "}
              {range.from ? `${formatShortDay(range.from)} – ${formatShortDay(range.to ?? today)}` : "any date"} · click
              a row for the shipment details and history
            </span>
            <span className="ml-auto">Only lines shipped in this app · the old workbook's rows are in History</span>
          </footer>
        </>
      )}
      {shown ? (
        <ShipmentSheet
          row={shown}
          pickup={pickupsById.get(shown.shipment.pickupId) ?? null}
          shippingRow={shippedRow(shown) ?? null}
          onClose={() => setDetails(null)}
        />
      ) : null}
      {flow.dialogs}
    </div>
  );
}

/** A day range: the Ship log's ship dates, the System log's change dates. */
export function DayRangePicker({
  label: prefix,
  value,
  today,
  onChange,
}: {
  label: string;
  value: ShipRange;
  today: string;
  onChange: (r: ShipRange) => void;
}) {
  const message = useMessage();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const presets = [
    ["7d", "Last 7 days"],
    ["30d", "Last 30 days"],
    ["90d", "Last 90 days"],
    ["any", "Any date"],
  ] as const;
  const label =
    value.from || value.to
      ? `${value.from ? formatShortDay(value.from) : "…"} – ${value.to ? formatShortDay(value.to) : "…"}${value.preset === "7d" ? " (last 7 days)" : ""}`
      : "Any date";
  const custom = async () => {
    const a = parseDateInput(from, "routedOn", today);
    const b = parseDateInput(to || formatShortDay(today), "routedOn", today);
    if (!a.ok || !b.ok || !a.value || !b.value)
      return message.error("Can't filter by these dates", "Enter both dates like 10/01 and 10/13.");
    if (a.value > b.value)
      return message.error("Can't filter by these dates", "The “from” date is after the “to” date.");
    onChange({ preset: "custom", from: a.value, to: b.value });
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <ToolbarButton
            icon={<CalendarIcon />}
            label={`${prefix}: ${label}`}
            hint={`Filter: ${prefix.toLowerCase()}`}
          />
        }
      />
      <PopoverContent align="start" className="w-72 gap-3 p-3 text-[13px]">
        <div className="grid grid-cols-2 gap-1.5">
          {presets.map(([id, text]) => (
            <Button
              key={id}
              size="sm"
              variant={value.preset === id ? "default" : "outline"}
              onClick={() => {
                onChange(shipRange(id, today));
                setOpen(false);
              }}
            >
              {text}
            </Button>
          ))}
        </div>
        <DayInputs from={from} to={to} today={today} onFrom={setFrom} onTo={setTo} />
        <Button size="sm" variant="secondary" onClick={() => void custom()}>
          Apply dates
        </Button>
      </PopoverContent>
    </Popover>
  );
}

type Kind = "system" | "pickup" | "edit";
interface Col {
  id: string;
  header: string;
  kind: Kind;
  width: number;
  /** The pickup's field (light teal) or the row's (white) an edit writes. */
  /** A line's divisions (`DivisionText`): abbreviations and codes, the full names on hover. */
  divs?: (r: ScheduleRow) => readonly DivisionRef[];
  /** Shown as the line's source badge. */
  badge?: (r: ScheduleRow) => LineSource;
  pickupField?: PickupField;
  rowField?: RowField;
  value: (r: ScheduleRow) => string;
  align?: "right";
}

const day = (d: string | null | undefined) => (d ? formatDay(d) : "");
const num = (n: number | null) => (n === null || n === undefined ? "" : n.toLocaleString("en-US"));

/** The Perris Shipping Schedule's columns, in its order, without CO. (Isaac). */
export const SCHEDULE_COLUMNS: Col[] = [
  {
    id: "date",
    header: "Date",
    kind: "pickup",
    width: 96,
    pickupField: "pickupDate",
    value: (r) => day(r.pickup.pickupDate),
  },
  { id: "customer", header: "Customer", kind: "system", width: 170, value: (r) => shownCustomerText(r.line) },
  {
    id: "source",
    header: "Source",
    kind: "system",
    width: 86,
    value: (r) => r.line.source,
    badge: (r) => r.line.source,
  },
  {
    id: "division",
    header: "Division",
    kind: "system",
    width: 120,
    value: (r) => r.line.divisions.join(", "),
    divs: (r) => r.line.divs,
  },
  { id: "po", header: "PO #", kind: "system", width: 150, value: (r) => poText(r.line) },
  { id: "pt", header: "PT # / SO #", kind: "system", width: 150, value: (r) => ptText(r.line) },
  {
    id: "ctns",
    header: "CTNS",
    kind: "edit",
    width: 70,
    rowField: "ctns",
    align: "right",
    value: (r) => num(r.row.ctns),
  },
  {
    id: "plts",
    header: "PLTS",
    kind: "edit",
    width: 70,
    rowField: "plts",
    align: "right",
    // The line's one value, the Worklist's PE Order Pallets (017): no longer the row's own.
    value: (r) => num(r.line.fields.pallets === "" ? null : Number(r.line.fields.pallets)),
  },
  {
    id: "loadRef",
    header: "PICK UP # / SHIPMENT # / BOL #",
    kind: "pickup",
    width: 210,
    pickupField: "loadRef",
    value: (r) => r.pickup.loadRef,
  },
  {
    id: "carrier",
    header: "Carrier",
    kind: "pickup",
    width: 150,
    pickupField: "carrier",
    value: (r) => r.pickup.carrier,
  },
  {
    id: "appTime",
    header: "App Time",
    kind: "pickup",
    width: 86,
    pickupField: "appTime",
    value: (r) => timeShown(r.pickup.appTime),
  },
  { id: "loader", header: "Loader", kind: "edit", width: 110, rowField: "loader", value: (r) => r.row.loader },
  { id: "inTime", header: "In:", kind: "edit", width: 70, rowField: "inTime", value: (r) => timeShown(r.row.inTime) },
  {
    id: "outTime",
    header: "Out:",
    kind: "edit",
    width: 70,
    rowField: "outTime",
    value: (r) => timeShown(r.row.outTime),
  },
  { id: "trailer", header: "Trailer#", kind: "edit", width: 100, rowField: "trailer", value: (r) => r.row.trailer },
  { id: "door", header: "Door", kind: "pickup", width: 66, pickupField: "door", value: (r) => r.pickup.door },
  {
    id: "wmsAsnTrip",
    header: "WMS / ASN / TRIP",
    kind: "edit",
    width: 130,
    rowField: "wmsAsnTrip",
    value: (r) => r.row.wmsAsnTrip,
  },
  { id: "notes", header: "Notes", kind: "edit", width: 200, rowField: "notes", value: (r) => r.row.notes },
];

const kindClass: Record<Kind, string> = { system: "cell-locked", pickup: "cell-pickup", edit: "cell-edit" };
const headClass: Record<Kind, string> = {
  system: "bg-locked before:bg-slate-400",
  pickup: "bg-pickup before:bg-teal-500",
  edit: "bg-background before:bg-emerald-400",
};

/** Row fields as text limits (`entities.ts`). */
const MAX: Partial<Record<RowField | PickupField, number>> = {
  loader: 100,
  trailer: 40,
  wmsAsnTrip: 100,
  notes: 500,
  carrier: 100,
  door: 20,
  loadRef: 100,
};

function ScheduleGrid({
  columns,
  rows,
  all,
  today,
  canWrite,
  selected,
  onSelect,
  onShip,
  onEditPickup,
  onEditCarrier,
  onEditRow,
  onEditPallets,
  onRemove,
  carriers,
  lineState,
}: {
  /** A line an action is saving (item 1): its row marked, a spinner for its checkbox. */
  lineState: (line: LineView) => LineState | undefined;
  /** The columns shown, in this person's order (its Columns menu). */
  columns: readonly Col[];
  /** The Carrier list, for the Carrier cell. */
  carriers: CarrierSourceApi;
  rows: ScheduleRow[];
  /** Every row, whatever the filters: a pickup's checks look at all its lines. */
  all: ScheduleRow[];
  today: string;
  /** False for a Viewer: no cell opens, no Ship truck, no Mark shipped or × (their column goes). */
  canWrite: boolean;
  selected: Record<string, boolean>;
  onSelect: (s: Record<string, boolean>) => void;
  onShip: (rows: ScheduleRow[]) => void;
  onEditPickup: (pickup: PickupRecord, field: PickupField, value: string) => Promise<unknown>;
  /** The pickup's carrier, picked from the source list (item 8); null for none. */
  onEditCarrier: (pickup: PickupRecord, pick: CarrierPick | null) => Promise<unknown>;
  onEditRow: (row: ScheduleRow, field: RowField, value: string | number | null) => void;
  /** PLTS: the line's PE Order Pallets (017), one value per line. */
  onEditPallets: (row: ScheduleRow, pallets: string) => Promise<unknown>;
  onRemove: (row: ScheduleRow) => Promise<unknown>;
}) {
  const message = useMessage();
  /** The cell being edited; `typed`: the character that started the edit (as in Excel). */
  const [editing, setEditing] = useState<{ row: string; col: string; typed?: string } | null>(null);
  const groups = scheduleGroups(rows, today);
  // Tab / Shift+Tab: the next / previous editable cell, row after row, as on the Worklist.
  const order = groups.flatMap((g) => g.rows);
  const editable = columns.filter((c) => c.kind !== "system");
  const nextCell = (rowId: string, colId: string, step: 1 | -1) => {
    const cells = order.flatMap((r) => editable.map((c) => ({ row: r.row.id, col: c.id })));
    const at = cells.findIndex((x) => x.row === rowId && x.col === colId);
    return cells[at + step] ?? null;
  };
  /**
   * The selected cell, as the Worklist's cursor: a click, the keys (`cell-keys.ts`) or an edit move it,
   * and it keeps its blue border while the focus is elsewhere. Read-only (grey) cells are selected too.
   */
  const [cursor, setCursor] = useState<{ row: string; col: string } | null>(null);
  /** The cursor's row's place in `order`: where it goes on when its row leaves. */
  const cursorAt = useRef(0);
  const gridRef = useRef<HTMLDivElement>(null);
  /**
   * Selects a cell and gives it the focus, so the keys go on working there: `now` for a cell already
   * on the page (a key's move); else on the next frame, once an editor has closed — its blur saves.
   */
  const focusCell = (rowId: string, colId: string, now = false) => {
    setCursor({ row: rowId, col: colId });
    const focus = () => document.querySelector<HTMLElement>(`[data-schedule-cell="${rowId}:${colId}"]`)?.focus();
    if (now) focus();
    else requestAnimationFrame(focus);
  };
  const moveFrom = (rowId: string, colId: string, k: CellKey) => {
    if (k.kind !== "move" && k.kind !== "edge") return;
    const at = { row: order.findIndex((r) => r.row.id === rowId), col: columns.findIndex((c) => c.id === colId) };
    const to = cellAfter(at, k, order.length, columns.length);
    const row = order[to.row];
    const col = columns[to.col];
    if (row && col) focusCell(row.row.id, col.id, true);
  };
  // The cursor's row shipped, was taken off or moved to another pickup (new rows), or its column was
  // hidden: the row now at its place, the first column. Back into the grid if the focus went with it.
  const rowKey = order.map((r) => r.row.id).join(" ");
  const colKey = columns.map((c) => c.id).join(" ");
  useEffect(() => {
    if (!cursor) return;
    const at = order.findIndex((r) => r.row.id === cursor.row);
    if (at >= 0) cursorAt.current = at;
    if (at >= 0 && columns.some((c) => c.id === cursor.col)) return;
    const row = order[at >= 0 ? at : Math.min(cursorAt.current, order.length - 1)];
    const col = columns.find((c) => c.id === cursor.col) ?? columns[0];
    if (!row || !col) return setCursor(null);
    const now = document.activeElement;
    if (!now || now === document.body || gridRef.current?.contains(now)) focusCell(row.row.id, col.id);
    else setCursor({ row: row.row.id, col: col.id });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the rows' and columns' ids only
  }, [rowKey, colKey]);
  /** A row's button (Mark shipped, Ship truck, take off) keeps the focus in its row's cell: its dialog gives it back there. */
  const keepInRow = (e: MouseEvent, r: ScheduleRow | undefined) => {
    if (!r) return;
    e.preventDefault();
    const col = cursor && columns.some((c) => c.id === cursor.col) ? cursor.col : columns[0]?.id;
    if (col) focusCell(r.row.id, col, true);
  };
  // The pinned right part (Mark shipped, ×): only for someone who may write.
  const actions = canWrite ? 150 : 0;
  const viewOnly = useViewOnlyNotice();
  const total = 34 + 30 + columns.reduce((n, c) => n + c.width, 0) + actions;

  /**
   * Checks a typed value; the big red message (or "Are you sure?") when it can't go as it is.
   * False: refused — the editor stays open with the text, as on the Worklist.
   */
  const refuse = async (title: string, body: string, lines?: readonly string[]) => {
    await message.error(title, body, lines);
    return false;
  };
  const commit = async (r: ScheduleRow, c: Col, raw: string): Promise<boolean> => {
    const text = raw.trim();
    // "In:" and "Out:" are the sheet's headers; in a message, without the colon.
    const name = c.header.replace(/:$/, "");
    const where = [`${r.line.customerName} · ${lineRef(r.line)}`];
    if (c.pickupField) {
      const field = c.pickupField;
      let value = text;
      const lines = all.filter((x) => x.pickup.id === r.pickup.id).map((x) => x.line);
      if (field === "pickupDate") {
        const parsed = parseDateInput(text, "appointmentDate", today);
        if (!parsed.ok || !parsed.value)
          return refuse("Can't save Date", parsed.ok ? "The pickup needs a date, like 10/16" : parsed.message, where);
        value = parsed.value;
      }
      if (field === "appTime") {
        const t = parseTime(text);
        if (!t.ok) return refuse("Can't save App Time", t.message, where);
        value = t.value;
      }
      if ((MAX[field] ?? 500) < value.length)
        return refuse(`Can't save ${name}`, `${name} is at most ${MAX[field]} characters.`, where);
      if (value === (r.pickup[field] ?? "")) return true;
      if (field === "pickupDate" || field === "loadRef") {
        // Only what this edit causes: "not routed" was confirmed when the lines were scheduled.
        const checked = checkPickupEdit(
          field,
          {
            pickupDate: field === "pickupDate" ? value : (r.pickup.pickupDate ?? ""),
            loadRef: field === "loadRef" ? value : r.pickup.loadRef,
          },
          lines,
          today,
        );
        if (checked.errors.length) return refuse(`Can't save ${name}`, checked.errors[0]!, checked.errors.slice(1));
        if (
          checked.warnings.length &&
          !(await message.confirm("Are you sure?", `Every line on this pickup changes. Check these first:`, {
            lines: checked.warnings,
            confirmLabel: "Save anyway",
          }))
        )
          return false;
      }
      // The carrier: one of A2000's or Deposco's (its key from the list), or none — never typed text.
      if (field === "carrier") {
        const o = carriers.options.find((x) => x.key === value);
        if (value && !o)
          return refuse("Can't save Carrier", "Pick a carrier from the list: A2000's and Deposco's only.", where);
        await onEditCarrier(r.pickup, o ? carrierPick(o) : null);
        return true;
      }
      await onEditPickup(r.pickup, field, value);
      return true;
    }
    const field = c.rowField!;
    let value: string | number | null = text;
    if (field === "ctns" || field === "plts") {
      const n = parseWhole(text, name);
      if (!n.ok) return refuse(`Can't save ${name}`, n.message, where);
      value = n.value;
      if (field === "plts") {
        // The line's PE Order Pallets: one value, saved on the line (its System log entry, its Undo).
        const pallets = value === null ? "" : String(value);
        if (pallets !== r.line.fields.pallets) await onEditPallets(r, pallets);
        return true;
      }
    } else if (field === "inTime" || field === "outTime") {
      const t = parseTime(text);
      if (!t.ok) return refuse(`Can't save ${name}`, t.message, where);
      value = t.value;
      const problem = field === "inTime" ? checkInOut(t.value, r.row.outTime) : checkInOut(r.row.inTime, t.value);
      if (problem) return refuse(`Can't save ${name}`, problem, where);
    } else if ((MAX[field] ?? 500) < text.length)
      return refuse(`Can't save ${name}`, `${name} is at most ${MAX[field]} characters.`, where);
    if ((value ?? "") === (r.row[field] ?? "")) return true;
    onEditRow(r, field, value);
    return true;
  };

  const startEdit = (r: ScheduleRow, c: Col, typed?: string) => {
    // A Viewer: the View only toast, once a visit; the cursor stays where it is.
    if (!canWrite) return viewOnly();
    if (c.kind === "system")
      return void message
        .error(
          `${c.header.replace(/:$/, "")} can't be typed here`,
          `${c.header.replace(/:$/, "")} comes from the Worklist (A2000). Change the line there.`,
        )
        .then(() => focusCell(r.row.id, c.id));
    setCursor({ row: r.row.id, col: c.id });
    setEditing({ row: r.row.id, col: c.id, ...(typed !== undefined ? { typed } : {}) });
  };

  /**
   * Delete / Backspace (`cell-keys.ts`): the cell emptied through the same checks as a typed value, as on
   * the Worklist — Date refuses (a pickup needs one), Carrier saves none; a grey cell has the message.
   */
  const clear = async (r: ScheduleRow, c: Col) => {
    if (c.kind === "system" || !canWrite) return startEdit(r, c);
    await commit(r, c, "");
    focusCell(r.row.id, c.id);
  };

  const ids = rows.map((r) => r.row.id);
  const allTicked = ids.length > 0 && ids.every((id) => selected[id]);
  return (
    <div
      ref={gridRef}
      className="routing-grid relative max-h-[calc(100dvh-24rem)] min-h-64 overflow-auto rounded-md border"
      data-testid="shipping-schedule"
    >
      <div style={{ width: total, minWidth: "100%" }}>
        <div role="row" className="bg-background sticky top-0 z-20 flex h-10 border-b">
          <div className="cell-head bg-background justify-center" style={{ width: 34 }}>
            <Checkbox
              aria-label="Select every row"
              checked={allTicked}
              onCheckedChange={(v) => onSelect(v ? Object.fromEntries(ids.map((id) => [id, true])) : {})}
            />
          </div>
          <div className="cell-head bg-background" style={{ width: 30 }} />
          {columns.map((c) => (
            <div
              key={c.id}
              role="columnheader"
              title={
                c.kind === "system"
                  ? "From the Worklist — locked"
                  : c.kind === "pickup"
                    ? "One value for the whole pickup"
                    : "Typed per row"
              }
              className={cn(
                "cell-head relative px-2 font-medium before:absolute before:inset-x-0 before:top-0 before:h-[3px]",
                headClass[c.kind],
              )}
              style={{ width: c.width }}
            >
              <span className="truncate">{c.header}</span>
            </div>
          ))}
          {canWrite ? (
            <div className="cell-head bg-background sticky right-0 justify-center font-medium" style={{ width: 150 }}>
              Ship
            </div>
          ) : null}
        </div>
        {groups.length === 0 ? (
          <p className="text-muted-foreground p-6 text-sm">
            {canWrite
              ? "Nothing is scheduled. Select lines on the Worklist and click Schedule pickup, or add a line below."
              : "Nothing is scheduled."}
          </p>
        ) : null}
        {groups.map((g) => {
          const pickups = [...new Map(g.rows.map((r) => [r.pickup.id, r.pickup])).values()];
          return (
            <div key={g.day} role="rowgroup">
              <div
                className={cn(
                  "sticky left-0 flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-3 py-1.5 text-sm",
                  g.late ? "group-late" : g.day === today ? "bg-blue-50" : "bg-muted/60",
                )}
                data-testid="schedule-group"
              >
                <span className={cn("font-semibold", g.day === today && "text-blue-700")}>{g.label}</span>
                <span className="text-muted-foreground text-xs">{g.counts}</span>
                {g.late ? <span className="text-xs font-medium">Past date — still not shipped</span> : null}
                {/* Beside the date's label, so it shows without scrolling right. */}
                <span className="flex flex-wrap gap-1">
                  {(canWrite ? pickups : []).map((p) => {
                    const truck = all.filter((r) => r.pickup.id === p.id);
                    return (
                      <Button
                        key={p.id}
                        size="xs"
                        variant="outline"
                        className="gap-1"
                        onMouseDown={(e) =>
                          keepInRow(
                            e,
                            g.rows.find((r) => r.pickup.id === p.id),
                          )
                        }
                        onClick={() => onShip(truck)}
                        title="Mark every line on this pickup shipped"
                      >
                        <TruckIcon className="size-3.5" />
                        Ship truck · {p.loadRef || p.carrier || p.id} ({truck.length})
                      </Button>
                    );
                  })}
                </span>
              </div>
              {g.rows.map((r) => {
                const warn = rowWarnings(r.line, r.pickup);
                const saving = lineState(r.line);
                return (
                  <div
                    key={r.row.id}
                    role="row"
                    data-schedule-row={r.row.id}
                    aria-busy={saving === "saving" || undefined}
                    className={cn(
                      "routing-row relative flex h-[37px] border-b",
                      selected[r.row.id] && "row-selected",
                      lineStateClass(saving),
                    )}
                  >
                    <div className="cell justify-center" style={{ width: 34 }}>
                      {saving === "saving" ? (
                        <SavingMark />
                      ) : (
                        <Checkbox
                          aria-label="Select row"
                          checked={!!selected[r.row.id]}
                          onCheckedChange={(v) => onSelect({ ...selected, [r.row.id]: Boolean(v) })}
                        />
                      )}
                    </div>
                    <div className="cell justify-center" style={{ width: 30 }}>
                      {warn.length ? (
                        <Tooltip>
                          <TooltipTrigger
                            render={<TriangleAlertIcon className="size-4 text-amber-500" aria-label="Check this row" />}
                          />
                          <TooltipContent>{warn.join(" · ")}</TooltipContent>
                        </Tooltip>
                      ) : null}
                    </div>
                    {columns.map((c, ci) => {
                      const isEditing = editing?.row === r.row.id && editing.col === c.id;
                      const isCursor = cursor?.row === r.row.id && cursor.col === c.id;
                      // One cell takes Tab into the grid: the cursor, else the first.
                      const tabStop = cursor ? isCursor : r === order[0] && ci === 0;
                      return (
                        <div
                          key={c.id}
                          role="gridcell"
                          aria-readonly={c.kind === "system" || !canWrite}
                          tabIndex={tabStop ? 0 : -1}
                          title={!canWrite && c.kind !== "system" ? "View only" : undefined}
                          className={cn(
                            // Clear of the sticky header, and of the Ship column when there is one.
                            "cell relative scroll-mt-10 px-2 outline-none",
                            canWrite ? "scroll-mr-[150px]" : "cursor-default",
                            kindClass[c.kind],
                            c.align === "right" && "justify-end",
                            isCursor && "cell-cursor",
                            isEditing && "cell-editing",
                          )}
                          style={{ width: c.width }}
                          data-schedule-cell={`${r.row.id}:${c.id}`}
                          onFocus={() => !isCursor && setCursor({ row: r.row.id, col: c.id })}
                          onDoubleClick={() => startEdit(r, c)}
                          onKeyDown={(e) => {
                            if (isEditing) return;
                            // Ctrl+C with no text selected copies the cell's value, grey cells too.
                            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "c") {
                              if (window.getSelection()?.toString()) return;
                              e.preventDefault();
                              return void navigator.clipboard.writeText(c.value(r));
                            }
                            const k = cellKey(e);
                            if (!k) return;
                            e.preventDefault();
                            if (k.kind === "edit") startEdit(r, c, k.typed);
                            else if (k.kind === "clear") void clear(r, c);
                            else moveFrom(r.row.id, c.id, k);
                          }}
                        >
                          {isEditing ? (
                            <InlineInput
                              initial={
                                c.id === "date"
                                  ? r.pickup.pickupDate
                                    ? formatShortDay(r.pickup.pickupDate)
                                    : ""
                                  : c.value(r)
                              }
                              typed={editing.typed}
                              label={c.header}
                              picker={
                                c.id === "date"
                                  ? "date"
                                  : c.id === "carrier"
                                    ? "carrier"
                                    : c.id === "inTime" || c.id === "outTime"
                                      ? "time"
                                      : undefined
                              }
                              today={today}
                              carriers={carriers}
                              onCommit={async (v, move) => {
                                const ok = await commit(r, c, v);
                                if (!ok) return false;
                                const next = move ? nextCell(r.row.id, c.id, move) : null;
                                setEditing(next);
                                if (next) setCursor(next);
                                else focusCell(r.row.id, c.id);
                                return true;
                              }}
                              onCancel={() => {
                                setEditing(null);
                                focusCell(r.row.id, c.id);
                              }}
                            />
                          ) : c.badge ? (
                            <SourceBadge source={c.badge(r)} />
                          ) : c.divs ? (
                            <DivisionText divs={c.divs(r)} />
                          ) : (
                            <span className="min-w-0 truncate" title={c.value(r)}>
                              {c.value(r)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {canWrite ? (
                      <div className="cell bg-background sticky right-0 justify-center gap-1" style={{ width: 150 }}>
                        <Button
                          size="xs"
                          variant="outline"
                          className="gap-1"
                          onMouseDown={(e) => keepInRow(e, r)}
                          onClick={() => onShip([r])}
                        >
                          <TruckIcon className="size-3.5" />
                          Mark shipped
                        </Button>
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          aria-label="Take off the pickup"
                          title="Take this line off the pickup"
                          onMouseDown={(e) => keepInRow(e, r)}
                          onClick={() => void onRemove(r)}
                        >
                          <XIcon />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A cell's editor: Enter or Tab saves, Esc cancels, leaving the cell saves. A refused value keeps
 * it open with the text: after "OK, I'll fix it" the focus is back in it, the text selected.
 */
function InlineInput({
  initial,
  typed,
  label,
  picker,
  today,
  carriers,
  onCommit,
  onCancel,
}: {
  initial: string;
  /** A character typed on the cell: it replaces the value, the caret after it. */
  typed?: string;
  label: string;
  /** Date: the calendar under the cell; Carrier: A2000's and Deposco's carriers; Time: hours, minutes and AM / PM (`TimeEditor`). */
  picker?: "date" | "carrier" | "time";
  today: string;
  carriers: CarrierSourceApi;
  /** Resolves true once saved (or unchanged), false when refused. `move`: Tab (1) or Shift+Tab (-1). */
  onCommit: (v: string, move: 0 | 1 | -1) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [text, setText] = useState(typed ?? initial);
  const ref = useRef<HTMLInputElement>(null);
  const list = useCarrierList(
    text,
    carriers.options,
    carriers.options.find((o) => o.name === initial)?.key ?? "",
    typed !== undefined,
  );
  /** Alt+↓ took the keyboard into the calendar. */
  const [keys, setKeys] = useState(false);
  // While a value is checked the message takes the focus: that blur is not a second save.
  const checking = useRef(false);
  const save = async (move: 0 | 1 | -1 = 0, value = text) => {
    if (checking.current) return;
    checking.current = true;
    const ok = await onCommit(value, move);
    checking.current = false;
    if (!ok) refocusEditor(ref.current);
  };
  /**
   * Enter or Tab: the carrier highlighted in a list that was used (its key), else what is typed — a
   * Carrier cell left as it was is unchanged, one cleared is none, other text is refused.
   */
  const saveTyped = (move: 0 | 1 | -1) => {
    const picked = picker === "carrier" ? list.picked() : null;
    if (picked !== null) setText(picked.name);
    void save(move, picked?.key ?? text);
  };
  if (picker === "time")
    return (
      <TimeEditor
        value={initial}
        initial={typed}
        label={label}
        inputRef={ref}
        className="h-full w-full bg-transparent px-2 outline-none"
        commit={(v, m) => void save(m === "right" ? 1 : m === "left" ? -1 : 0, v)}
        cancel={onCancel}
      />
    );
  return (
    <>
      <input
        ref={ref}
        autoFocus
        aria-label={label}
        className="h-full w-full bg-transparent px-2 outline-none"
        value={text}
        onFocus={(e) => {
          if (typed === undefined) e.target.select();
          else e.target.setSelectionRange(e.target.value.length, e.target.value.length);
        }}
        onChange={(e) => {
          setText(e.target.value);
          list.typedIn();
        }}
        onBlur={(e) => {
          // Into the calendar from the keyboard is not leaving the cell.
          if (!(e.relatedTarget instanceof Element && e.relatedTarget.closest("[data-cell-panel]"))) void save();
        }}
        onKeyDown={(e) => {
          if (picker === "carrier" && list.arrows(e)) return;
          if (picker === "date" && e.key === "ArrowDown" && e.altKey) {
            e.preventDefault();
            setKeys(true);
            return;
          }
          if (e.key === "Escape") {
            e.preventDefault();
            onCancel();
          } else if (e.key === "Enter") {
            e.preventDefault();
            saveTyped(0);
          } else if (e.key === "Tab") {
            e.preventDefault();
            saveTyped(e.shiftKey ? -1 : 1);
          }
        }}
      />
      {picker === "date" ? (
        <Floating anchor={ref} width={290}>
          <CalendarPanel
            key={keys ? "keys" : "mouse"}
            selected={dayOfText(text, today)}
            today={today}
            autoFocus={keys}
            onPick={(d) => void save(0, typedDay(d))}
            onEscape={() => {
              setKeys(false);
              ref.current?.focus();
            }}
          />
        </Floating>
      ) : picker === "carrier" ? (
        <Floating anchor={ref}>
          {carriers.status === "failed" ? (
            <div className="px-3 py-2">
              <CarriersUnavailable carriers={carriers} />
            </div>
          ) : (
            <CarrierOptions
              list={list}
              onPick={(o) => {
                setText(o.name);
                void save(0, o.key);
              }}
              onClear={initial ? () => void save(0, "") : undefined}
            />
          )}
        </Floating>
      ) : null}
    </>
  );
}

/**
 * Add a line right on the sheet (spec §3.13, Figure 33): its PO / PT / SO # must be on an open
 * Worklist line. It goes on the pickup of that date and PICK UP # when there is one, else a new one.
 */
function AddLine({
  lines,
  find,
  pickups,
  rowsOf,
  today,
  carriers,
  onAdd,
}: {
  /** This page's lines: a typed number on a shipped one says so. */
  lines: readonly LineView[];
  /** The open lines around a typed number, from the server (the page holds only its pickups' lines). */
  find: (q: string) => Promise<LineView[]>;
  pickups: readonly PickupRecord[];
  rowsOf: (pickupId: string) => number;
  today: string;
  /** The carriers of A2000 and Deposco (item 8). */
  carriers: CarrierSourceApi;
  onAdd: (line: LineView, target: { input: PickupInput } | { pickup: PickupRecord; rows: number }) => Promise<boolean>;
}) {
  const message = useMessage();
  const empty = { date: "", typed: "", loadRef: "", carrier: null as CarrierPick | null, time: "", door: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const typedRef = useRef<HTMLInputElement>(null);
  const set = (k: Exclude<keyof typeof empty, "carrier">) => (e: { target: { value: string } }) =>
    setF({ ...f, [k]: e.target.value });

  const add = async () => {
    const parsed = parseDateInput(f.date, "appointmentDate", today);
    if (!parsed.ok || !parsed.value)
      return message.error("Can't add the line", parsed.ok ? "Type the pickup date, like 10/16" : parsed.message);
    const t = parseTime(f.time);
    if (!t.ok) return message.error("Can't add the line", t.message);
    setBusy(true);
    let found: LineView[];
    let finished: LineView[];
    try {
      const near = await find(f.typed.trim());
      found = findOpenLines(near, f.typed);
      finished = findFinishedLines([...near, ...lines], f.typed);
    } catch (e) {
      setBusy(false);
      return message.error("Can't look the line up", e instanceof Error ? e.message : String(e));
    }
    setBusy(false);
    // Which line, on which pickup, or why not (`planAddLine`): refused → back in the PO / PT / SO # field.
    const plan = planAddLine({
      typed: f.typed,
      open: found,
      finished,
      pickups,
      pickupDate: parsed.value,
      loadRef: f.loadRef,
      today,
    });
    if ("refuse" in plan) {
      await message.error(plan.refuse.title, plan.refuse.body, plan.refuse.lines);
      return refocusEditor(typedRef.current);
    }
    const { line, pickup: same, checked } = plan;
    if (checked.errors.length) return message.error("Can't add the line", checked.errors[0], checked.errors.slice(1));
    if (
      checked.warnings.length &&
      !(await message.confirm("Are you sure?", "Check these before you go on:", {
        lines: checked.warnings,
        confirmLabel: "Add anyway",
      }))
    )
      return;
    setBusy(true);
    const ok = await onAdd(
      line,
      same
        ? { pickup: same, rows: rowsOf(same.id) }
        : {
            input: {
              pickupDate: parsed.value,
              appTime: t.value,
              carrier: f.carrier?.carrier ?? "",
              carrierCode: f.carrier?.carrierCode ?? "",
              carrierSource: f.carrier?.carrierSource ?? "",
              door: f.door,
              loadRef: f.loadRef,
            },
          },
    );
    setBusy(false);
    if (ok) setF(empty);
  };

  const field = (label: string, node: ReactNode, className = "") => (
    <label className={cn("space-y-1 text-xs font-medium", className)}>
      <span>{label}</span>
      {node}
    </label>
  );
  return (
    <div className="rounded-md border p-3" data-testid="schedule-add-line">
      <p className="mb-2 flex items-center gap-1.5 text-sm">
        <PlusIcon className="size-4" />
        <b>Add a line to the schedule</b>
        <span className="text-muted-foreground">— type the PO, PT or SO #; the line must be open on the Worklist</span>
      </p>
      <div className="flex flex-wrap items-end gap-2">
        {field(
          "Date",
          <DateField
            className="w-24"
            aria-label="Date"
            today={today}
            value={f.date}
            onChange={(date) => setF({ ...f, date })}
          />,
        )}
        {field(
          "PO # / PT # / SO #",
          <Input
            ref={typedRef}
            className="h-8 w-56"
            placeholder="e.g. 11775640 or 1808101"
            value={f.typed}
            onChange={set("typed")}
          />,
        )}
        {field(
          "PICK UP # / SHIPMENT # / BOL #",
          <Input
            className="h-8 w-52"
            maxLength={100}
            placeholder="optional"
            value={f.loadRef}
            onChange={set("loadRef")}
          />,
        )}
        {field(
          "Carrier",
          <div className="w-56">
            <CarrierField value={f.carrier} onChange={(carrier) => setF({ ...f, carrier })} carriers={carriers} />
          </div>,
        )}
        {field(
          "App Time",
          // What will be stored, as it will show: "14:30" → "2:30 PM" once the box is left.
          <Input
            className="h-8 w-20"
            placeholder="10:00 AM"
            value={f.time}
            onChange={set("time")}
            onBlur={() => setF((now) => ({ ...now, time: typedTimeShown(now.time) }))}
          />,
        )}
        {field("Door", <Input className="h-8 w-16" maxLength={20} value={f.door} onChange={set("door")} />)}
        <Button size="sm" disabled={busy} onClick={() => void add()} data-testid="schedule-add-button">
          <PlusIcon />
          Add line
        </Button>
      </div>
    </div>
  );
}

/** The Ship log's columns: the schedule's, plus Tracking # and Shipped by. A header is its id. */
function shipLogColumns(
  pickupsById: ReadonlyMap<string, PickupRecord>,
  shippedRow: (r: ShipLogRow) => ShippingRowRecord | undefined,
) {
  const cols: {
    header: string;
    width: number;
    value: (r: ShipLogRow) => string;
    align?: "right";
    divs?: (r: ShipLogRow) => readonly DivisionRef[];
    badge?: (r: ShipLogRow) => LineSource;
  }[] = [
    { header: "Customer", width: 170, value: (r) => shownCustomerText(r.line) },
    { header: "Source", width: 86, value: (r) => r.line.source, badge: (r) => r.line.source },
    {
      header: "Division",
      width: 120,
      value: (r) => r.line.divisions.join(", "),
      divs: (r) => r.line.divs,
    },
    { header: "PO #", width: 150, value: (r) => poText(r.line) },
    { header: "PT # / SO #", width: 150, value: (r) => ptText(r.line) },
    { header: "CTNS", width: 64, value: (r) => num(r.shipment.ctns), align: "right" },
    { header: "PLTS", width: 64, value: (r) => num(r.shipment.plts), align: "right" },
    { header: "PICK UP # / SHIPMENT # / BOL #", width: 210, value: (r) => r.shipment.loadRef },
    { header: "Carrier", width: 140, value: (r) => r.shipment.carrier },
    { header: "App Time", width: 80, value: (r) => timeShown(pickupsById.get(r.shipment.pickupId)?.appTime) },
    { header: "Loader", width: 100, value: (r) => r.shipment.loader },
    { header: "In:", width: 64, value: (r) => timeShown(r.shipment.inTime) },
    { header: "Out:", width: 64, value: (r) => timeShown(r.shipment.outTime) },
    { header: "Trailer#", width: 96, value: (r) => r.shipment.trailer },
    { header: "Door", width: 60, value: (r) => r.shipment.door },
    { header: "WMS / ASN / TRIP", width: 120, value: (r) => shippedRow(r)?.wmsAsnTrip ?? r.line.wmsNo },
    { header: "Notes", width: 160, value: (r) => r.shipment.notes },
    { header: "Tracking #", width: 150, value: (r) => r.shipment.tracking },
    { header: "Shipped by", width: 140, value: (r) => r.line.finished?.by ?? "" },
  ];
  return cols.map((c) => ({ ...c, id: c.header }));
}

/** The Ship log's columns as its Columns menu lists them. */
const SHIP_LOG_MENU = shipLogColumns(new Map(), () => undefined);

/** The Ship log's columns: the schedule's, plus Tracking # and Shipped by. */
function ShipLog({
  shownIds,
  rows,
  pickupsById,
  shippedRow,
  onOpen,
}: {
  /** The columns shown, in this person's order (its Columns menu). */
  shownIds: readonly string[];
  rows: ShipLogRow[];
  pickupsById: ReadonlyMap<string, PickupRecord>;
  shippedRow: (r: ShipLogRow) => ShippingRowRecord | undefined;
  onOpen: (r: ShipLogRow) => void;
}) {
  const groups = shipLogGroups(rows);
  const all = shipLogColumns(pickupsById, shippedRow);
  const cols = shownIds.flatMap((id) => all.filter((c) => c.id === id));
  const total = cols.reduce((n, c) => n + c.width, 0);
  return (
    <div
      className="routing-grid relative max-h-[calc(100dvh-20rem)] min-h-64 overflow-auto rounded-md border"
      data-testid="ship-log"
    >
      <div style={{ width: total, minWidth: "100%" }}>
        <div role="row" className="bg-locked sticky top-0 z-20 flex h-10 border-b">
          {cols.map((c) => (
            <div key={c.header} role="columnheader" className="cell-head px-2 font-medium" style={{ width: c.width }}>
              <span className="truncate">{c.header}</span>
            </div>
          ))}
        </div>
        {groups.length === 0 ? (
          <p className="text-muted-foreground p-6 text-sm">No line was marked shipped in these dates.</p>
        ) : null}
        {groups.map((g) => (
          <div key={g.day} role="rowgroup">
            <div className="bg-muted/60 sticky left-0 border-b px-3 py-1.5 text-sm" data-testid="ship-log-group">
              <b>{g.label.split(" · ")[0]}</b>
              <span className="text-muted-foreground"> · {g.label.split(" · ").slice(1).join(" · ")}</span>
            </div>
            {g.rows.map((r) => (
              <div
                key={r.shipment.id}
                role="row"
                tabIndex={0}
                className="routing-row flex h-[37px] cursor-pointer border-b"
                onClick={() => onOpen(r)}
                onKeyDown={(e) => e.key === "Enter" && onOpen(r)}
              >
                {cols.map((c) => (
                  <div
                    key={c.header}
                    role="gridcell"
                    className={cn("cell px-2", c.align === "right" && "justify-end")}
                    style={{ width: c.width }}
                  >
                    {c.badge ? (
                      <SourceBadge source={c.badge(r)} />
                    ) : c.divs ? (
                      <DivisionText divs={c.divs(r)} />
                    ) : (
                      <span className="min-w-0 truncate" title={c.value(r)}>
                        {c.value(r)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
